/**
 * Data-minimisation gate for the "relação nominal de concluintes".
 *
 * Res. CEE 44/2020, Art. 4º requires only the NOMINAL list. Most Ensino Médio
 * graduates are minors, and the list is destined for permanent, unrestricted
 * publication in the official gazette. Anything beyond name + course + year is
 * an unnecessary exposure of a child's personal data.
 *
 * So this does not warn — it REJECTS the file. See documentacoes/03-lgpd-e-dados.md § 1.2.
 *
 * Pure module, 100% coverage required (CLAUDE.md § Convenções).
 */

import { CAMPOS_PROIBIDOS_CONCLUINTES } from '../constantes/index.js'

export interface ColunaAnalisada {
  /** Header exactly as it came from the uploaded file. */
  original: string
  /** Normalised form used for matching. */
  normalizado: string
}

export interface ResultadoValidacaoColunas {
  valido: boolean
  /** Forbidden columns found, with the rule they matched. */
  proibidas: Array<{ coluna: string; termo: string }>
  /** Required columns that are missing. */
  faltantes: string[]
  /** Columns that are neither required nor forbidden — kept, but reported. */
  desconhecidas: string[]
  /** pt-BR message ready to show the school. */
  mensagem: string
}

export const COLUNAS_OBRIGATORIAS = ['nome', 'curso', 'ano'] as const

/**
 * Normalises a header for comparison: lowercase, no accents, no punctuation,
 * separators collapsed to a single underscore.
 */
export function normalizarCabecalho(valor: string): string {
  return String(valor ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // strips combining diacritics
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

/** Portuguese connectives that carry no meaning in a column header. */
const CONECTIVOS = new Set(['de', 'do', 'da', 'dos', 'das', 'no', 'na', 'em', 'para'])

/**
 * Splits a normalised header into tokens.
 *
 * Merges a single-letter token into the next one so that "E-mail" → e_mail →
 * ["email"]. Without this, an e-mail column would slip past the filter — which
 * is exactly the kind of silent leak this module exists to prevent.
 */
function tokenizar(normalizado: string): string[] {
  const brutos = normalizado.split('_').filter(Boolean)
  const saida: string[] = []
  for (let i = 0; i < brutos.length; i++) {
    const atual = brutos[i]!
    const proximo = brutos[i + 1]
    if (atual.length === 1 && proximo !== undefined) {
      saida.push(atual + proximo)
      i++
    } else {
      saida.push(atual)
    }
  }
  return saida
}

function casaSequencia(tokensColuna: string[], tokensTermo: string[]): boolean {
  if (tokensTermo.length === 1) return tokensColuna.includes(tokensTermo[0]!)
  for (let i = 0; i <= tokensColuna.length - tokensTermo.length; i++) {
    if (tokensTermo.every((t, j) => tokensColuna[i + j] === t)) return true
  }
  return false
}

/**
 * Matches a normalised header against a forbidden term.
 *
 * Token-boundary matching, never plain substring — otherwise "Anotações" would
 * be rejected because it contains the letters of "nota". Connectives are
 * skipped so "Data de Nascimento" matches the term "data_nascimento" and
 * "Título de Eleitor" matches "titulo_eleitor".
 */
function contemTermoProibido(normalizado: string, termo: string): boolean {
  if (normalizado === termo) return true

  const tokensTermo = termo.split('_').filter(Boolean)
  // CAMPOS_PROIBIDOS_CONCLUINTES has no empty entries; guard kept for safety.
  /* v8 ignore next */
  if (tokensTermo.length === 0) return false

  const brutos = normalizado.split('_').filter(Boolean)
  const tokenizados = tokenizar(normalizado)
  const semConectivos = tokenizados.filter((t) => !CONECTIVOS.has(t))

  return (
    casaSequencia(brutos, tokensTermo) ||
    casaSequencia(tokenizados, tokensTermo) ||
    casaSequencia(semConectivos, tokensTermo)
  )
}

/**
 * Validates the header row of an uploaded concluintes file.
 * Returns a structured result; the caller rejects the upload when !valido.
 */
export function validarColunasConcluintes(cabecalhos: readonly string[]): ResultadoValidacaoColunas {
  const colunas: ColunaAnalisada[] = (cabecalhos ?? [])
    .map((c) => ({ original: String(c ?? ''), normalizado: normalizarCabecalho(c) }))
    .filter((c) => c.normalizado.length > 0)

  const proibidas: Array<{ coluna: string; termo: string }> = []
  for (const coluna of colunas) {
    for (const termo of CAMPOS_PROIBIDOS_CONCLUINTES) {
      if (contemTermoProibido(coluna.normalizado, termo)) {
        proibidas.push({ coluna: coluna.original, termo })
        break // one reason per column is enough
      }
    }
  }

  const temNome = colunas.some((c) => c.normalizado.split('_').includes('nome'))
  const temCurso = colunas.some((c) => c.normalizado.split('_').includes('curso'))
  const temAno = colunas.some((c) =>
    c.normalizado.split('_').some((t) => t === 'ano' || t === 'conclusao'),
  )

  const faltantes: string[] = []
  if (!temNome) faltantes.push('nome do concluinte')
  if (!temCurso) faltantes.push('curso')
  if (!temAno) faltantes.push('ano de conclusão')

  const proibidasSet = new Set(proibidas.map((p) => p.coluna))
  const desconhecidas = colunas
    .filter((c) => !proibidasSet.has(c.original))
    .filter((c) => {
      const tokens = c.normalizado.split('_')
      return !(
        tokens.includes('nome') ||
        tokens.includes('curso') ||
        tokens.includes('ano') ||
        tokens.includes('conclusao') ||
        tokens.includes('turma') ||
        tokens.includes('ordem')
      )
    })
    .map((c) => c.original)

  const valido = proibidas.length === 0 && faltantes.length === 0

  const partes: string[] = []
  if (proibidas.length > 0) {
    const lista = proibidas.map((p) => `"${p.coluna}"`).join(', ')
    partes.push(
      `Arquivo recusado: contém dado pessoal desnecessário à publicação (${lista}). ` +
        'A Resolução CEE 44/2020 exige apenas a relação nominal. Remova essas colunas e reenvie.',
    )
  }
  if (faltantes.length > 0) {
    partes.push(`Colunas obrigatórias ausentes: ${faltantes.join(', ')}.`)
  }
  if (valido) {
    partes.push('Arquivo válido.')
    if (desconhecidas.length > 0) {
      partes.push(`Colunas não reconhecidas serão ignoradas: ${desconhecidas.join(', ')}.`)
    }
  }

  return { valido, proibidas, faltantes, desconhecidas, mensagem: partes.join(' ') }
}

/** Thrown by the intake when a file carries forbidden personal data. */
export class ArquivoConcluintesRecusadoError extends Error {
  readonly resultado: ResultadoValidacaoColunas

  constructor(resultado: ResultadoValidacaoColunas) {
    super(resultado.mensagem)
    this.name = 'ArquivoConcluintesRecusadoError'
    this.resultado = resultado
  }
}
